import React, { useEffect, useRef, useState } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, RefreshCw, Plus, X, ThumbsUp, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

function BulletList({ items, setItems, kind, disabled, idPrefix }) {
  const onUpdate = (i, v) => setItems(items.map((x, j) => (j === i ? v : x)));
  const onRemove = (i) => setItems(items.filter((_, j) => j !== i));
  const onAdd = () => setItems([...items, '']);

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-xs text-[#94A3B8] italic" data-testid={`${idPrefix}-empty`}>No bullets yet</p>
      )}
      {items.map((b, i) => (
        <div key={i} className="flex items-start gap-2" data-testid={`${idPrefix}-row-${i}`}>
          <span className={`mt-2 w-2 h-2 rounded-full shrink-0 ${kind === 'strength' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          <Textarea
            value={b}
            onChange={(e) => onUpdate(i, e.target.value)}
            rows={2}
            disabled={disabled}
            placeholder={kind === 'strength' ? 'Strength bullet…' : 'Opportunity bullet…'}
            data-testid={`${idPrefix}-input-${i}`}
            className="resize-none"
          />
          <Button size="sm" variant="ghost" onClick={() => onRemove(i)} disabled={disabled} className="text-[#94A3B8] hover:text-rose-600" data-testid={`${idPrefix}-remove-${i}`}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={onAdd} disabled={disabled || items.length >= 10} data-testid={`${idPrefix}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add bullet
      </Button>
    </div>
  );
}

function DiffPicker({ existing, proposed, onApply, onCancel }) {
  // Per-bullet picker: each proposed bullet has its own keep/replace decision.
  const [pickStrengths, setPickStrengths] = useState(proposed.strengths.map(() => true));
  const [pickOpps, setPickOpps] = useState(proposed.opportunities.map(() => true));
  const [keepExisting, setKeepExisting] = useState(true);

  const apply = () => {
    const newS = [
      ...(keepExisting ? existing.strengths : []),
      ...proposed.strengths.filter((_, i) => pickStrengths[i]),
    ];
    const newO = [
      ...(keepExisting ? existing.opportunities : []),
      ...proposed.opportunities.filter((_, i) => pickOpps[i]),
    ];
    onApply({ strengths: newS, opportunities: newO });
  };

  return (
    <div className="bg-[#FAFAFC] border-2 border-dashed border-[#7C3AED] rounded-lg p-4 space-y-4" data-testid="satisaction-diff-picker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold text-[#0F172A] inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#7C3AED]" /> DeepQual<sup>®</sup> suggestions — pick which to include
        </p>
        <label className="text-xs text-[#475569] inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={keepExisting} onChange={(e) => setKeepExisting(e.target.checked)} data-testid="diff-keep-existing" />
          Keep my existing bullets
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1.5">Proposed strengths</p>
          <ul className="space-y-1.5">
            {proposed.strengths.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={pickStrengths[i]}
                  onChange={(e) => setPickStrengths(pickStrengths.map((x, j) => (j === i ? e.target.checked : x)))}
                  className="mt-0.5"
                  data-testid={`diff-pick-strength-${i}`}
                />
                <span className="text-[#0F172A]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5">Proposed opportunities</p>
          <ul className="space-y-1.5">
            {proposed.opportunities.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={pickOpps[i]}
                  onChange={(e) => setPickOpps(pickOpps.map((x, j) => (j === i ? e.target.checked : x)))}
                  className="mt-0.5"
                  data-testid={`diff-pick-opp-${i}`}
                />
                <span className="text-[#0F172A]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="diff-cancel">Cancel</Button>
        <Button size="sm" onClick={apply} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="diff-apply">
          <Check className="w-3.5 h-3.5 mr-1" /> Apply selection
        </Button>
      </div>
    </div>
  );
}

export default function SatisactionSection({ find, onChange }) {
  const responseId = find?.response_id || find?.public_id;
  const initial = find?.satisaction || { strengths: [], opportunities: [] };
  const [strengths, setStrengths] = useState(initial.strengths || []);
  const [opportunities, setOpportunities] = useState(initial.opportunities || []);
  const [generating, setGenerating] = useState(false);
  const [diff, setDiff] = useState(null);
  const [savingState, setSavingState] = useState('idle');
  const debounceRef = useRef(null);

  // Re-sync when parent doc changes (e.g. after generation accept).
  useEffect(() => {
    setStrengths(find?.satisaction?.strengths || []);
    setOpportunities(find?.satisaction?.opportunities || []);
  }, [find?.satisaction?.last_edited_at, find?.satisaction?.generated_at]);

  // Debounced auto-save (2s).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Skip save if it matches what's already in the doc.
    const cur = find?.satisaction || {};
    if (
      JSON.stringify(strengths) === JSON.stringify(cur.strengths || []) &&
      JSON.stringify(opportunities) === JSON.stringify(cur.opportunities || [])
    ) {
      setSavingState('idle');
      return;
    }
    setSavingState('typing');
    debounceRef.current = setTimeout(async () => {
      setSavingState('saving');
      try {
        const res = await fetch(`${API}/qa/finds/${responseId}/satisaction`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ strengths, opportunities }),
        });
        const data = await res.json();
        if (!res.ok) { toast.error('Could not save satisACTION®'); setSavingState('idle'); return; }
        onChange({ satisaction: data.satisaction });
        setSavingState('saved');
        setTimeout(() => setSavingState('idle'), 1500);
      } catch {
        toast.error('Could not save satisACTION®');
        setSavingState('idle');
      }
    }, 2000);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line
  }, [strengths, opportunities]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/qa/finds/${responseId}/satisaction/generate`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'satisACTION® generation failed'); return; }
      if (!data.proposed.strengths.length && !data.proposed.opportunities.length) {
        toast.error('DeepQual® returned no suggestions — try regenerating or write your own.');
        return;
      }
      // If empty current, accept all directly. Otherwise show picker.
      if (!strengths.length && !opportunities.length) {
        setStrengths(data.proposed.strengths);
        setOpportunities(data.proposed.opportunities);
        toast.success('Suggestions applied');
      } else {
        setDiff({ existing: data.existing, proposed: data.proposed });
      }
    } catch { toast.error('satisACTION® generation failed'); }
    finally { setGenerating(false); }
  };

  const applyDiff = ({ strengths: s, opportunities: o }) => {
    setStrengths(s);
    setOpportunities(o);
    setDiff(null);
    toast.success('Selection applied — saving…');
  };

  return (
    <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 space-y-4" data-testid="satisaction-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Montserrat'] text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#7C3AED]" /> satisACTION<sup>®</sup>
          </h2>
          <p className="text-xs text-[#64748B] mt-0.5">Strengths + Opportunities — this is what the client sees on the published report.</p>
        </div>
        <div className="flex items-center gap-2">
          {savingState === 'typing' && <span className="text-xs text-[#94A3B8]" data-testid="satisaction-save-typing">Typing…</span>}
          {savingState === 'saving' && <span className="text-xs text-[#1A3E6F]" data-testid="satisaction-save-saving">Saving…</span>}
          {savingState === 'saved' && <span className="text-xs text-emerald-600" data-testid="satisaction-save-saved">Saved</span>}
          <Button onClick={generate} disabled={generating} variant="outline" size="sm" data-testid="satisaction-generate-btn">
            {generating ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {generating ? 'Generating…' : (strengths.length || opportunities.length ? 'Regenerate' : 'Generate with DeepQual®')}
          </Button>
        </div>
      </div>

      {diff && <DiffPicker existing={diff.existing} proposed={diff.proposed} onApply={applyDiff} onCancel={() => setDiff(null)} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-2 border-l-2 border-emerald-500 pl-3" data-testid="satisaction-strengths">
          <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1">
            <ThumbsUp className="w-3.5 h-3.5" /> Strengths
          </p>
          <BulletList items={strengths} setItems={setStrengths} kind="strength" disabled={generating} idPrefix="strength" />
        </div>
        <div className="space-y-2 border-l-2 border-amber-500 pl-3" data-testid="satisaction-opportunities">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Opportunities
          </p>
          <BulletList items={opportunities} setItems={setOpportunities} kind="opportunity" disabled={generating} idPrefix="opportunity" />
        </div>
      </div>
    </section>
  );
}
